

import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Loader2 } from "lucide-react";

import {
  useCreateEnglishQuestionMutation,
} from "../../slices/englishQuestionApiSlice";

import EnglishQuestionForm from "./components/EnglishQuestionForm";

const EnglishQuestionCreate = () => {
  const navigate = useNavigate();

  const [createEnglishQuestion, { isLoading }] =
    useCreateEnglishQuestionMutation();

  const handleSubmit = async (formData) => {
    try {
      await createEnglishQuestion(formData).unwrap();

      navigate("/english-questions");
    } catch (error) {
      console.error("Failed to create English question:", error);
    }
  };

  const handleCancel = () => {
    navigate("/english-questions");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* =====================================================
          Header
      ====================================================== */}
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-5 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <button
                type="button"
                onClick={handleCancel}
                className="mt-1 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900"
                aria-label="Back to English questions"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>

              <div>
                <h1 className="text-xl font-bold text-slate-900 sm:text-2xl">
                  Create English Question
                </h1>

                <p className="mt-1 text-sm text-slate-500">
                  Add a new English question to the question bank.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleCancel}
                disabled={isLoading}
                className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Cancel
              </button>

              <button
                type="submit"
                form="english-question-form"
                disabled={isLoading}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    Create Question
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* =====================================================
          Main Content
      ====================================================== */}
      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 px-5 py-4 sm:px-6">
            <h2 className="text-base font-semibold text-slate-900">
              Question Details
            </h2>

            <p className="mt-1 text-sm text-slate-500">
              Configure the question type, content, answers, explanation,
              difficulty, marks, chapter and group.
            </p>
          </div>

          <div className="p-5 sm:p-6">
            <EnglishQuestionForm
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              isLoading={isLoading}
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default EnglishQuestionCreate;

